import React, { useEffect, useState, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import logger from "@/utils/logger";
import SEO from "@/components/seo/SEO";
import { PLANS } from "@/config/stripe";
import { detectBrowser } from "@/utils/browser";

// Define constants for verification flow flags (similar to password reset flow)
const VERIFICATION_FLOW_FLAG = 'petdoc_in_verification_flow';

const VerifyHandler: React.FC = () => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use useLayoutEffect to set flags before any rendering happens
  // This ensures flags are set before RequireAuth can evaluate them
  useLayoutEffect(() => {
    // Check if this appears to be a verification callback from URL hash
    const isVerificationCallback = window.location.hash.includes('type=signup');
    
    if (isVerificationCallback) {
      // Set flags in both localStorage and sessionStorage to mark this as a verification flow
      // These flags will be checked by RequireAuth to exempt this route from redirection
      localStorage.setItem(VERIFICATION_FLOW_FLAG, 'true');
      sessionStorage.setItem(VERIFICATION_FLOW_FLAG, 'true');
      
      logger.info('VerifyHandler: Set verification flow flags');
    } else {
      logger.warn('VerifyHandler: No verification parameters detected in URL hash');
    }
    
    // Clean up flags when component unmounts
    return () => {
      localStorage.removeItem(VERIFICATION_FLOW_FLAG);
      sessionStorage.removeItem(VERIFICATION_FLOW_FLAG);
      logger.info('VerifyHandler: Cleaned up verification flow flags');
    };
  }, []);

  // Main verification handling effect
  useEffect(() => {
    const handleVerification = async () => {
      setIsProcessing(true);
      
      try {
        logger.info("VerifyHandler: Processing verification from URL");
        
        // Check if we have auth parameters in the URL
        if (!window.location.hash.includes("access_token")) {
          logger.error("VerifyHandler: No access token found in URL");
          setError("No verification parameters found in URL");
          setIsProcessing(false);
          return;
        }

        // Extract confirmation type from the URL to confirm this is a signup verification
        const isSignupConfirmation = window.location.hash.includes("type=signup");
        if (!isSignupConfirmation) {
          logger.info("VerifyHandler: Not a signup confirmation, redirecting to dashboard");
          navigate("/dashboard");
          return;
        }

        // The session should be automatically established by Supabase's client library
        // when it detects the hash parameters in the URL
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          logger.error("VerifyHandler: Error getting session from URL", { error });
          setError(`Verification failed: ${error.message}`);
          setIsProcessing(false);
          return;
        }

        if (!data.session) {
          logger.error("VerifyHandler: No session returned from getSessionFromUrl");
          setError("Verification failed: No session established");
          setIsProcessing(false);
          return;
        }

        // Successfully verified and signed in
        logger.info("VerifyHandler: Successfully verified email and signed in");
        
        // Detect browser for logging and debugging
        const { name: browserName, version: browserVersion } = detectBrowser();
        const isIOSSafari = browserName === 'Safari' && navigator.userAgent.includes('iPhone');
        logger.info("VerifyHandler: Browser detection", { browserName, browserVersion, isIOSSafari });
        
        // Check if we have a pendingPriceId from the subscription selection flow
        let pendingPriceId = localStorage.getItem("pendingPriceId");
        let pendingPriceIdSource = 'localStorage';
        
        // Get promoCode from localStorage (using the same direct approach as plan type)
        let promoCode = localStorage.getItem("pendingPromoCode");
        let stripeCouponId = localStorage.getItem("pendingPromoCouponId");
        let promoCodeSource = 'localStorage';
        
        logger.info("VerifyHandler: Initial promoCode check", { 
          promoCode: promoCode || "None", 
          stripeCouponId: stripeCouponId || "None", 
          source: promoCodeSource 
        });
        
        // If not found in localStorage, check the database (fallback for iOS Safari)
        if (!pendingPriceId && data.session?.user) {
          logger.info("VerifyHandler: No pendingPriceId in localStorage, checking database");
          
          try {
            // Use 'as any' to work around TypeScript not knowing about the new table yet
            // This will be fixed when the TypeScript definitions are updated
            const { data: dbData, error: dbError } = await (supabase
              .from('user_pending_subscriptions' as any)
              .select('pending_price_id')
              .eq('email', data.session.user.email.toLowerCase())
              .single() as any);
              
            if (dbError) {
              logger.error("VerifyHandler: Error checking database for pendingPriceId", { error: dbError });
            } else if (dbData?.pending_price_id) {
              pendingPriceId = dbData.pending_price_id;
              pendingPriceIdSource = 'database';
              logger.info("VerifyHandler: Retrieved pendingPriceId from database", { pendingPriceId });
              
              // Clean up the database record
              await (supabase
                .from('user_pending_subscriptions' as any)
                .delete()
                .eq('email', data.session.user.email.toLowerCase()) as any);
              
              logger.info("VerifyHandler: Deleted database record after retrieval");
            }
          } catch (dbFetchError) {
            logger.error("VerifyHandler: Unexpected error when checking database", { error: dbFetchError });
          }
        }
        
        if (pendingPriceId) {
          logger.info("VerifyHandler: Found pendingPriceId, redirecting to checkout", { 
            pendingPriceId, 
            source: pendingPriceIdSource,
            browserName,
            isIOSSafari
          });
          
          // Create a checkout session and redirect to Stripe
          try {
            const origin = window.location.origin;
            // Redirect to subscription success page after successful checkout
            const successUrl = `${origin}/subscription/success?session_refresh=true`;
            const cancelUrl = `${origin}/pricing?subscription=canceled`;
            
            logger.info("VerifyHandler: Creating checkout session", { pendingPriceId });
            
            // Get user info from the session
            const { data: userData } = await supabase.auth.getUser();
            if (!userData.user?.email) {
              logger.error("VerifyHandler: No user email found in session");
              toast.error("Could not create checkout session. Please try again from your account.");
              navigate("/dashboard");
              return;
            }
            
            // Determine the plan type from the priceId
            let planType = null;
            for (const [key, plan] of Object.entries(PLANS)) {
              if (plan.id === pendingPriceId) {
                planType = key;
                break;
              }
            }
            
            if (!planType) {
              logger.error("VerifyHandler: Could not determine plan type from priceId", { pendingPriceId });
              toast.error("Invalid subscription plan. Please select a plan from your dashboard.");
              navigate("/dashboard");
              return;
            }
            
            const requestBody = {
              priceId: pendingPriceId,
              planType,
              successUrl,
              cancelUrl,
              customerEmail: userData.user.email,
              promoCode: promoCode || null,         // Always include promoCode (even if null)
              stripeCouponId: stripeCouponId || null // Always include stripeCouponId (even if null)
            };
            
            // Log the exact values being sent in the request
            console.error("VERIFY HANDLER DEBUG - Checkout request body:", JSON.stringify(requestBody, null, 2));
            
            logger.info("VerifyHandler: Sending request to create-checkout-session", requestBody);
            
            const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke(
              "create-checkout-session",
              { body: requestBody }
            );
            
            if (checkoutError) {
              // Still log the error for debugging, but don't show an error toast to the user
              logger.error("VerifyHandler: Error creating checkout session", { error: checkoutError });
              
              // Just redirect to dashboard without showing an error toast
              logger.info("VerifyHandler: Redirecting to dashboard despite checkout error");
              navigate("/dashboard");
              return;
            }
            
            if (checkoutData?.url) {
              // Clear the pendingPriceId since we're handling it now
              localStorage.removeItem("pendingPriceId");
              
              // Redirect to Stripe checkout
              logger.info("VerifyHandler: Redirecting to Stripe checkout", { url: checkoutData.url });
              window.location.href = checkoutData.url;
              return;
            } else {
              // Still log the error for debugging, but don't show an error toast to the user
              logger.error("VerifyHandler: No checkout URL returned", { checkoutData });
              
              // Just redirect to dashboard without showing an error toast
              logger.info("VerifyHandler: Redirecting to dashboard due to missing checkout URL");
              navigate("/dashboard");
              return;
            }
          } catch (e) {
            logger.error("VerifyHandler: Exception during checkout creation", { error: e });
            toast.error("An error occurred. Please try accessing checkout from your account.");
            navigate("/dashboard");
            return;
          }
        } else {
          // No pending price ID, just go to dashboard
          logger.info("VerifyHandler: No pendingPriceId found, redirecting to dashboard");
          navigate("/dashboard");
        }
      } catch (e) {
        // Catch any unexpected errors
        logger.error("VerifyHandler: Unexpected error during verification handling", { error: e });
        setError("An unexpected error occurred during verification");
        setIsProcessing(false);
      }
    };

    handleVerification();
  }, [navigate]);

  // Show a simple loading UI while processing
  if (isProcessing) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <SEO 
          title="Verifying Your Account | PetDocument" 
          description="Please wait while we verify your account."
        />
        <div className="bg-white p-8 rounded-2xl shadow-soft max-w-md w-full text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Verifying Your Account</h1>
          <p className="text-gray-600">Please wait while we complete your email verification...</p>
        </div>
      </div>
    );
  }

  // Show an error UI if something went wrong
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <SEO 
          title="Verification Error | PetDocument" 
          description="There was an error verifying your account."
        />
        <div className="bg-white p-8 rounded-2xl shadow-soft max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Verification Error</h1>
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={() => navigate("/login")}
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // This shouldn't normally render as we redirect before this point
  return null;
};

export default VerifyHandler;
